export const REJECTION_COPY = Object.freeze({
    MANAGED_AGENT_CONFIG_REJECTION_REASON_MALFORMED: "The composed document is not valid JSON.",
    MANAGED_AGENT_CONFIG_REJECTION_REASON_TOO_LARGE: "The composed policy is too large.",
    MANAGED_AGENT_CONFIG_REJECTION_REASON_TOO_DEEP: "The composed policy is nested too deeply.",
    MANAGED_AGENT_CONFIG_REJECTION_REASON_UNKNOWN_KEY: "This control produced a setting the server does not recognize.",
    MANAGED_AGENT_CONFIG_REJECTION_REASON_WRONG_TYPE: "This control produced the wrong value type.",
    MANAGED_AGENT_CONFIG_REJECTION_REASON_INVALID_VALUE: "This control produced a value outside the server catalog.",
    MANAGED_AGENT_CONFIG_REJECTION_REASON_EXECUTION_SURFACE: "This setting would introduce an execution surface.",
    MANAGED_AGENT_CONFIG_REJECTION_REASON_CAPTURE_KEY: "This setting overlaps with Origin-managed capture configuration.",
});

export const CUSTOMIZATION_SURFACE_COPY = Object.freeze({
    skills: {
        label: "Block user and project skills",
        description: "Skills supplied by plugins or managed settings remain available.",
    },
    agents: {
        label: "Block user and project subagents",
        description: "Subagents supplied by plugins or managed settings remain available.",
    },
    mcp: {
        label: "Prevent users and projects from adding MCP servers",
        description: "Only MCP servers supplied by plugins or managed settings reach the server filter above, including servers that match an Allow List.",
    },
});

export const RULE_INPUT_GUIDANCE = Object.freeze({
    Bash: {
        label: "Command pattern (optional)",
        help: "Enter only the command portion inside Bash(...). Add :* after a command prefix to match any continuation.",
        example: "git push:*",
    },
    Read: {
        label: "Path pattern (optional)",
        help: "Use a gitignore-style path. Paths are relative to the settings file; use // for an absolute path or ~/ for the user's home directory.",
        example: "./secrets/**",
    },
    Edit: {
        label: "Path pattern (optional)",
        help: "Use a gitignore-style path for files Claude Code may edit.",
        example: "docs/**",
    },
    Write: {
        label: "Path pattern (optional)",
        help: "Use a gitignore-style path for files Claude Code may write.",
        example: "./generated/**",
    },
    WebFetch: {
        label: "Domain pattern (optional)",
        help: "Prefix a hostname with domain: to match fetches to that domain.",
        example: "domain:example.com",
    },
    WebSearch: {
        label: "Specifier (optional)",
        help: "Leave this blank to match every WebSearch use.",
        example: "Leave blank to add WebSearch",
    },
    Task: {
        label: "Agent name (optional)",
        help: "Enter the configured subagent name, or leave blank to match every Task use.",
        example: "security-reviewer",
    },
    Skill: {
        label: "Skill name (optional)",
        help: "Enter the skill name, or leave blank to match every Skill use.",
        example: "code-review",
    },
});

export const AUTO_MODE_LISTS = Object.freeze({
    environment: {
        key: "environment",
        label: "Organization context",
        inputLabel: "Trusted environment description",
        addLabel: "Add context",
        placeholder: "Source control: github.example.com/acme-corp and all repos under it",
        help: "Describe trusted repositories, domains, cloud resources, internal services, and sensitive deployment targets in plain English.",
        example: "Trusted internal domains: *.internal.example.com",
    },
    hardDeny: {
        key: "hard_deny",
        label: "Always block",
        inputLabel: "Unconditional auto mode restriction",
        addLabel: "Add restriction",
        placeholder: "Never send repository contents to third-party code-review APIs",
        help: "The classifier will not approve a matching action, even when the user explicitly requests it. Use a managed Block rule above when the action must be blocked before the classifier runs.",
        example: "Never upload source code to public paste services",
    },
    softDeny: {
        key: "soft_deny",
        label: "Block unless explicitly requested",
        inputLabel: "Overridable auto mode restriction",
        addLabel: "Add restriction",
        placeholder: "Require explicit user intent before deploying to production",
        help: "The classifier blocks a matching action unless the user specifically requests it or an allow exception applies. Personal allow entries can also override this rule.",
        example: "Require explicit user intent before running a production migration",
    },
    allow: {
        key: "allow",
        label: "Allow exceptions",
        inputLabel: "Exception to an auto mode soft block",
        addLabel: "Add exception",
        placeholder: "Deploying to the staging namespace is allowed",
        help: "Use this only to clear a matching soft block for a routine action. It cannot override an auto mode hard block or a managed permission Block rule.",
        example: "Writing to s3://acme-scratch/ is allowed",
    },
});

export const MODEL_ALIAS_COPY = Object.freeze({
    best: { label: "Best", description: "Uses Fable where available, otherwise the latest Opus model." },
    fable: { label: "Fable", description: "For the longest and most demanding tasks, where available." },
    sonnet: { label: "Sonnet", description: "The current Sonnet model for everyday coding work." },
    opus: { label: "Opus", description: "The current Opus model for complex reasoning." },
    haiku: { label: "Haiku", description: "The current Haiku model for faster, simpler tasks." },
    "sonnet[1m]": { label: "Sonnet 1M", description: "Sonnet with an extended context window, where supported." },
    "opus[1m]": { label: "Opus 1M", description: "Opus with an extended context window, where supported." },
    opusplan: { label: "Opus Plan", description: "Uses Opus while planning and Sonnet while executing." },
});

export const DEFAULT_MODE_COPY = Object.freeze({
    "": {
        label: "Not configured",
        description: "No starting permission mode is written. Claude Code uses its own default, and user or project settings may still set one.",
    },
    default: {
        label: "Standard",
        description: "Ask the user when a permission rule does not already decide the action.",
    },
    auto: {
        label: "Auto",
        description: "The classifier reviews actions that ordinary permission rules did not resolve.",
    },
    plan: {
        label: "Plan only",
        description: "The session may read and plan, but it cannot change files until the user switches mode.",
    },
    dontAsk: {
        label: "Do not ask",
        description: "Deny actions that would otherwise prompt the user.",
    },
    acceptEdits: {
        label: "Accept file edits",
        description: "File edits are applied without asking. Other actions still follow permission rules.",
    },
    bypassPermissions: {
        label: "Bypass permissions",
        description: "Skip permission prompts. Prevent bypassing permissions disables this mode.",
    },
    manual: {
        label: "Manual",
        description: "Same starting behavior as Standard.",
    },
});

export const PROVIDER_ORDER = Object.freeze([
    "anthropic", "bedrock", "vertex", "foundry", "anthropicAws", "mantle", "customEndpoint", "gateway",
]);

export const PROVIDER_COPY = Object.freeze({
    anthropic: { label: "Anthropic API", description: "A claude.ai or Console sign-in, or an API key, on Anthropic's own host." },
    bedrock: { label: "Amazon Bedrock", description: "Claude through Amazon Bedrock." },
    vertex: { label: "Google Vertex", description: "Google Cloud's Agent Platform, formerly Vertex AI." },
    foundry: { label: "Microsoft Foundry", description: "Claude through Microsoft Foundry." },
    anthropicAws: { label: "Claude on AWS", description: "The Claude Platform on AWS." },
    mantle: { label: "Bedrock Mantle", description: "The Amazon Bedrock Mantle endpoint." },
    customEndpoint: { label: "Custom endpoint", description: "A gateway or provider endpoint pinned by a managed env block." },
    gateway: { label: "Cloud gateway", description: "A Cloud gateway sign-in." },
});

export const MODELS_MATCH_COPY = Object.freeze([
    { value: "prefix", label: "Allow later versions" },
    { value: "exact", label: "Exact versions only" },
]);

export const POLICY_CATEGORIES = Object.freeze([
    {
        id: "session",
        label: "Session setup",
    },
    {
        id: "permissions",
        label: "Tool permissions",
    },
    {
        id: "auto",
        label: "Auto mode",
    },
    {
        id: "inputs",
        label: "Content & resources",
    },
    {
        id: "external",
        label: "External & remote interactions",
    },
    {
        id: "messaging",
        label: "Messaging",
    },
]);
